import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, Input, Space, Table, Tag, Typography, Empty, Button, Popover, Popconfirm, message } from 'antd';
import { SearchOutlined, ReloadOutlined, InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { getUploadBatchesByPatient, deleteUploadBatch } from '../../actions/uploadBatchActions';
import { UPLOAD_BATCH_DELETE_RESET } from '../../constants/uploadBatchConstants';
import { FilePathCard } from '../crud/utils/renderUIComponents';
import { formatUkDateTime, formatUploadDisplayName } from '../../utils/functions';
import { isAttachmentUploadType } from '../../utils/uploadBatchType';
import { getUploadBatchFileIcon } from '../../screens/patients/uploadBatchFileIcon';
import ApiErrorAlert from '../common/ApiErrorAlert';
import { showApiError } from '../../utils/formatApiError';

const { Text } = Typography;

const STATUS_COLORS = {
  Pending: 'default',
  Processing: 'processing',
  Completed: 'success',
  Rejected: 'error',
  Failed: 'error',
};

/**
 * Per-patient uploaded files table.
 * Default: tenant GET /uploadbatch/patient/{patientId}.
 * Vault: pass loadBatches + batchesSelectorKey (e.g. vaultPatientUploadBatches); readOnly skips delete.
 */
const PatientUploadedFiles = ({
  patientId,
  patientIdentifier: patientIdentifierProp,
  loadBatches,
  batchesSelectorKey,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');

  const fetchBatches = loadBatches || getUploadBatchesByPatient;
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  const patient = useSelector((state) => state.patientDetails?.patient);
  const patientIdentifier = patientIdentifierProp
    || (patient && String(patient.id) === String(patientId) ? patient.patientIdentifier : '')
    || '';
  const batchesState = useSelector((state) =>
    batchesSelectorKey ? state[batchesSelectorKey] : state.uploadBatchByPatient) || {};
  const { loading, uploadBatches = [], error } = batchesState;
  const { loading: deleting, success: deleteSuccess, error: deleteError } = useSelector((state) => state.uploadBatchDelete) || {};

  useEffect(() => {
    if (patientId) {
      dispatch(fetchBatches(patientId));
    }
  }, [dispatch, patientId, fetchBatches]);

  useEffect(() => {
    if (readOnly) return;
    if (deleteSuccess) {
      message.success(t('Upload batch and its files were deleted'));
      dispatch({ type: UPLOAD_BATCH_DELETE_RESET });
      if (patientId) dispatch(fetchBatches(patientId));
    }
    if (deleteError) {
      showApiError(deleteError, t);
      dispatch({ type: UPLOAD_BATCH_DELETE_RESET });
    }
  }, [deleteSuccess, deleteError, dispatch, patientId, t, fetchBatches, readOnly]);

  const handleDelete = (id) => dispatch(deleteUploadBatch(id));

  const filtered = useMemo(() => {
    if (!search) return uploadBatches;
    const q = search.toLowerCase();
    const matches = (v) => v?.toLowerCase().includes(q);
    return uploadBatches.filter((b) =>
      matches(b.fileName) || matches(b.filePath) || matches(b.matFilePath) || matches(b.type)
      || matches(formatUploadDisplayName(b.fileName, patientIdentifier))
      || matches(formatUploadDisplayName(b.matFilePath?.split('/').pop(), patientIdentifier))
    );
  }, [uploadBatches, search, patientIdentifier]);

  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <Text strong>
            {t('Uploaded Files')}
            {!error && ` (${uploadBatches.length})`}
          </Text>
          <Popover
            content={
              <div style={{ maxWidth: 320 }}>
                {t('Study ZIPs are converted to .mat. PDF and DICOM are stored as-is and marked completed. Search by filename, path or type.')}
              </div>
            }
            title={t('About uploaded files')}
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
          </Popover>
        </Space>
      }
      extra={
        <Space>
          <Input
            allowClear
            size="small"
            placeholder={t('Search files...')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => dispatch(fetchBatches(patientId))}
          />
        </Space>
      }
      loading={loading}
      style={{ marginTop: 16 }}
    >
      <ApiErrorAlert error={error} />
      {error ? null : filtered.length === 0 ? (
        <Empty description={t('No files uploaded for this patient yet')} />
      ) : (
        <Table
          size="small"
          rowKey="id"
          dataSource={filtered}
          scroll={{ y: 320 }}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          expandable={{
            expandedRowRender: (batch) => (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {isAttachmentUploadType(batch.type) ? t('File') : t('Study zip')}
                  </Text>
                  <div><FilePathCard filePath={batch.filePath} emptyText={t('No file uploaded')} userInfo={userInfo} patientIdentifier={patientIdentifier} /></div>
                </div>
                {!isAttachmentUploadType(batch.type) && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Generated MAT file')}</Text>
                    <div><FilePathCard filePath={batch.matFilePath} emptyText={t('Not yet generated')} userInfo={userInfo} patientIdentifier={patientIdentifier} /></div>
                  </div>
                )}
              </Space>
            ),
          }}
          columns={[
            {
              title: t('Created'),
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 160,
              defaultSortOrder: 'descend',
              sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
              render: (date) => <Text style={{ fontSize: 12 }}>{formatUkDateTime(date)}</Text>,
            },
            {
              title: t('Type'),
              dataIndex: 'type',
              key: 'type',
              width: 100,
              render: (type) => <Tag>{type}</Tag>,
            },
            {
              title: t('File'),
              dataIndex: 'fileName',
              key: 'fileName',
              ellipsis: true,
              onCell: () => ({ style: { maxWidth: 320 } }),
              render: (fileName, record) => {
                const displayName = formatUploadDisplayName(fileName, patientIdentifier);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}>
                    {getUploadBatchFileIcon(record.fileFormat || fileName, { fontSize: 24, marginRight: 0 })}
                    <Text ellipsis={{ tooltip: fileName }} style={{ flex: 1, minWidth: 0 }}>{displayName || '-'}</Text>
                  </div>
                );
              },
            },
            {
              title: t('Status'),
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
            },
            ...(!readOnly ? [{
              title: t('Actions'),
              key: 'actions',
              width: 70,
              align: 'right',
              render: (_, batch) => (
                <Popconfirm
                  title={t('Delete upload batch?')}
                  description={t('This also deletes the file from SFTP. This cannot be undone.')}
                  onConfirm={() => handleDelete(batch.id)}
                  okText={t('Yes')}
                  cancelText={t('No')}
                  okButtonProps={{ danger: true }}
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} loading={deleting} />
                </Popconfirm>
              ),
            }] : []),
          ]}
        />
      )}
    </Card>
  );
};

export default PatientUploadedFiles;
